/**
 * Ciclos de estado de cuenta de tarjeta de crédito.
 *
 * El modelo mexicano tiene dos fechas por ciclo:
 *   - **corte**: cierra el periodo y fija el saldo a pagar,
 *   - **límite de pago**: corte + N días (20 hábiles por defecto).
 *
 * De aquí sale la regla que hace útil a PayBae: un gasto con tarjeta no baja el
 * dinero disponible el día que se hace, sino el día que vence su estado de
 * cuenta. Ubicar cada movimiento en su ciclo correcto es lo que permite avisar
 * con precisión cuánto hay que pagar y cuándo.
 */

import {
  type HolidaySet,
  NO_HOLIDAYS,
  addBusinessDays,
  adjustToBusinessDay,
  businessDaysUntil,
} from '../date/businessDays.js'
import {
  type PlainDate,
  addDays,
  addMonths,
  differenceInDays,
  getMonth,
  getYear,
  plainDate,
  withDayOfMonth,
} from '../date/plainDate.js'

export type PaymentTermType = 'BUSINESS_DAYS' | 'CALENDAR_DAYS'

export interface CardCycleConfig {
  /**
   * Día del mes en que corta la tarjeta, 1–31.
   * Se recorta al último día en meses cortos, así que 31 corta el 28 o 29 en
   * febrero. `0` significa explícitamente «último día del mes».
   */
  statementDay: number
  /** Plazo para pagar tras el corte. Por defecto 20. */
  paymentTermDays: number
  /** Cómo se cuenta el plazo. Por defecto días hábiles. */
  paymentTermType: PaymentTermType
  /**
   * Si un gasto hecho **el mismo día del corte** entra en ese estado de cuenta.
   * Por defecto `false`: la mayoría de los bancos en México lo mandan al
   * siguiente periodo. Es configurable por tarjeta porque no todos coinciden.
   */
  cutoffInclusive: boolean
}

export const DEFAULT_CYCLE_CONFIG: Pick<
  CardCycleConfig,
  'paymentTermDays' | 'paymentTermType' | 'cutoffInclusive'
> = {
  paymentTermDays: 20,
  paymentTermType: 'BUSINESS_DAYS',
  cutoffInclusive: false,
}

export interface StatementCycle {
  /** Primer día del periodo: el siguiente al corte anterior. */
  cycleStart: PlainDate
  /** Último día del periodo. */
  cutoffDate: PlainDate
  /** Fecha límite según el plazo de la tarjeta. */
  dueDate: PlainDate
  /**
   * Último día hábil en o antes del límite. Cuando el límite cae en fin de
   * semana o festivo, pagar antes es la única opción segura: la transferencia
   * puede no acreditarse el mismo día.
   */
  safePaymentDate: PlainDate
}

/** Fecha de corte de un mes concreto, ya recortada a los días que existen. */
export function cutoffInMonth(year: number, month: number, statementDay: number): PlainDate {
  if (!Number.isInteger(statementDay) || statementDay < 0 || statementDay > 31) {
    throw new RangeError(`Día de corte fuera de rango: ${statementDay}`)
  }
  return withDayOfMonth(plainDate(year, month, 1), statementDay)
}

/** Calcula la fecha límite de pago a partir del corte. */
export function computeDueDate(
  cutoffDate: PlainDate,
  config: Pick<CardCycleConfig, 'paymentTermDays' | 'paymentTermType'>,
  holidays: HolidaySet = NO_HOLIDAYS,
): PlainDate {
  if (config.paymentTermDays < 0) {
    throw new RangeError(`El plazo de pago no puede ser negativo: ${config.paymentTermDays}`)
  }
  return config.paymentTermType === 'BUSINESS_DAYS'
    ? addBusinessDays(cutoffDate, config.paymentTermDays, holidays)
    : addDays(cutoffDate, config.paymentTermDays)
}

function buildCycle(
  cutoffDate: PlainDate,
  config: CardCycleConfig,
  holidays: HolidaySet,
): StatementCycle {
  const anchor = plainDate(getYear(cutoffDate), getMonth(cutoffDate), 1)
  const previousAnchor = addMonths(anchor, -1)
  const previousCutoff = cutoffInMonth(
    getYear(previousAnchor),
    getMonth(previousAnchor),
    config.statementDay,
  )
  const dueDate = computeDueDate(cutoffDate, config, holidays)

  return {
    cycleStart: addDays(previousCutoff, 1),
    cutoffDate,
    dueDate,
    safePaymentDate: adjustToBusinessDay(dueDate, 'PREVIOUS_BUSINESS_DAY', holidays),
  }
}

/**
 * Ciclo al que pertenece un movimiento.
 *
 * Es la función que decide en qué estado de cuenta cae cada gasto, y por lo
 * tanto cuándo hay que pagarlo.
 */
export function resolveStatement(
  transactionDate: PlainDate,
  config: CardCycleConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): StatementCycle {
  const year = getYear(transactionDate)
  const month = getMonth(transactionDate)
  const cutoffThisMonth = cutoffInMonth(year, month, config.statementDay)

  const belongsToThisMonth = config.cutoffInclusive
    ? transactionDate <= cutoffThisMonth
    : transactionDate < cutoffThisMonth

  if (belongsToThisMonth) {
    return buildCycle(cutoffThisMonth, config, holidays)
  }

  const nextAnchor = addMonths(plainDate(year, month, 1), 1)
  const cutoffNextMonth = cutoffInMonth(
    getYear(nextAnchor),
    getMonth(nextAnchor),
    config.statementDay,
  )
  return buildCycle(cutoffNextMonth, config, holidays)
}

/**
 * Último ciclo que **ya cortó** en o antes de `date`.
 *
 * Es distinto de `resolveStatement`, y confundirlos es un error caro:
 * `resolveStatement` responde «¿en qué estado de cuenta cae un gasto de hoy?»
 * —el ciclo abierto, que vence dentro de mes y medio—, mientras que esta
 * responde «¿qué debo pagar ahora?» —el ciclo cerrado, cuya fecha límite se
 * acerca—. Los recordatorios de pago y el dinero comprometido salen de aquí.
 */
export function lastClosedCycle(
  date: PlainDate,
  config: CardCycleConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): StatementCycle {
  const year = getYear(date)
  const month = getMonth(date)
  const cutoffThisMonth = cutoffInMonth(year, month, config.statementDay)

  if (cutoffThisMonth <= date) {
    return buildCycle(cutoffThisMonth, config, holidays)
  }

  const previousAnchor = addMonths(plainDate(year, month, 1), -1)
  return buildCycle(
    cutoffInMonth(getYear(previousAnchor), getMonth(previousAnchor), config.statementDay),
    config,
    holidays,
  )
}

/** Ciclo cuyo corte es el primero estrictamente posterior a `date`. */
export function nextCycleAfter(
  date: PlainDate,
  config: CardCycleConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): StatementCycle {
  const year = getYear(date)
  const month = getMonth(date)
  const cutoffThisMonth = cutoffInMonth(year, month, config.statementDay)

  if (date < cutoffThisMonth) {
    return buildCycle(cutoffThisMonth, config, holidays)
  }
  const nextAnchor = addMonths(plainDate(year, month, 1), 1)
  return buildCycle(
    cutoffInMonth(getYear(nextAnchor), getMonth(nextAnchor), config.statementDay),
    config,
    holidays,
  )
}

/**
 * Todos los ciclos cuyo corte cae dentro del rango, en orden.
 * Sirve para generar estados de cuenta hacia atrás al dar de alta una tarjeta
 * que ya venía usándose, y hacia adelante para proyectar vencimientos.
 */
export function generateCycles(
  from: PlainDate,
  to: PlainDate,
  config: CardCycleConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): StatementCycle[] {
  if (from > to) return []

  const cycles: StatementCycle[] = []
  let anchor = plainDate(getYear(from), getMonth(from), 1)
  const lastAnchor = plainDate(getYear(to), getMonth(to), 1)

  while (anchor <= lastAnchor) {
    const cutoff = cutoffInMonth(getYear(anchor), getMonth(anchor), config.statementDay)
    if (cutoff >= from && cutoff <= to) {
      cycles.push(buildCycle(cutoff, config, holidays))
    }
    anchor = addMonths(anchor, 1)
  }
  return cycles
}

export type StatementUrgency = 'OVERDUE' | 'DUE_TODAY' | 'DUE_SOON' | 'SCHEDULED'

export interface StatementCountdown {
  calendarDaysUntilDue: number
  businessDaysUntilDue: number
  /** Días naturales hasta la última fecha segura para pagar. */
  daysUntilSafePayment: number
  urgency: StatementUrgency
}

/**
 * Cuenta regresiva de un estado de cuenta.
 * `DUE_SOON` se activa a cinco días naturales, que es cuando arranca la
 * escalera de recordatorios.
 */
export function statementCountdown(
  today: PlainDate,
  cycle: StatementCycle,
  holidays: HolidaySet = NO_HOLIDAYS,
  dueSoonThresholdDays = 5,
): StatementCountdown {
  const calendarDaysUntilDue = differenceInDays(cycle.dueDate, today)
  const businessDays = businessDaysUntil(today, cycle.dueDate, holidays)
  const daysUntilSafePayment = differenceInDays(cycle.safePaymentDate, today)

  let urgency: StatementUrgency
  if (calendarDaysUntilDue < 0) urgency = 'OVERDUE'
  else if (calendarDaysUntilDue === 0) urgency = 'DUE_TODAY'
  else if (calendarDaysUntilDue <= dueSoonThresholdDays) urgency = 'DUE_SOON'
  else urgency = 'SCHEDULED'

  return {
    calendarDaysUntilDue,
    businessDaysUntilDue: businessDays,
    daysUntilSafePayment,
    urgency,
  }
}

/**
 * Crédito disponible. Se acota a cero porque una tarjeta sobregirada no otorga
 * capacidad de compra negativa: simplemente no queda nada.
 */
export function availableCredit(creditLimitCents: number, currentBalanceCents: number): number {
  return Math.max(0, creditLimitCents - currentBalanceCents)
}

/** Uso de la línea en porcentaje, acotado a 0–100 para la barra de la UI. */
export function creditUtilization(creditLimitCents: number, currentBalanceCents: number): number {
  if (creditLimitCents <= 0) return 0
  return Math.min(100, Math.max(0, (currentBalanceCents / creditLimitCents) * 100))
}

/** Un ciclo de la tarjeta, con lo que cerró y lo que se le ha abonado. */
export interface CardCycleSnapshot {
  cutoffDate: PlainDate
  closingBalanceCents: number
  paidAmountCents: number
}

/** Deuda de una tarjeta, desglosada por lo que la compone. */
export interface CardBalanceParts {
  /** Lo que se le debe al banco hoy. Es lo que consume la línea de crédito. */
  currentBalanceCents: number
  /** Cortes ya cerrados con saldo pendiente. */
  billedUnpaidCents: number
  /** Consumo del ciclo en curso, mensualidad a meses incluida. */
  openCycleCents: number
  /** Capital de compras a meses cuyos cortes todavía no llegan. */
  unbilledInstallmentsCents: number
}

/**
 * Arma la deuda de una tarjeta a partir de sus ciclos y del capital a meses que
 * todavía no se factura.
 *
 * Existe para que la regla viva en un solo sitio y se pueda probar. Había dos
 * versiones en la app —una en la pantalla de tarjetas y otra en el tablero— que
 * daban números distintos sobre la misma tarjeta, y **ninguna de las dos contaba
 * las compras a meses**: una mensualidad no tiene movimiento detrás, se deriva
 * del plan, así que una tarjeta con una compra a doce meses mostraba la línea
 * de crédito entera disponible.
 *
 * Las tres piezas no se solapan, y en eso está la corrección:
 *
 * - **Cortes ya cerrados** (`cutoffDate < openCutoff`): `cierre − pagado`, sin
 *   bajar de cero. El cierre ya trae dentro la mensualidad que ese corte cobró.
 * - **Ciclo abierto** (`cutoffDate === openCutoff`): lo consumido desde el
 *   último corte. Se deja con signo: una devolución que lo ponga en negativo sí
 *   compensa deuda anterior, que es lo que hace el banco.
 * - **Cortes futuros**: no suman por su fila. Lo que cobrarán viaja en
 *   `unbilledInstallmentsCents`, y contarlo por ambos lados duplicaría la deuda.
 *
 * El capital a meses entra completo desde el primer día porque así funciona una
 * línea de crédito: el banco reserva los $15,666 de una compra a doce meses el
 * día de la compra y los libera conforme se pagan, no $1,305 al mes.
 */
export function composeCardBalance(
  cycles: readonly CardCycleSnapshot[],
  openCutoff: PlainDate,
  unbilledInstallmentsCents: number,
): CardBalanceParts {
  let billedUnpaidCents = 0
  let openCycleCents = 0

  for (const cycle of cycles) {
    if (cycle.cutoffDate === openCutoff) {
      openCycleCents += cycle.closingBalanceCents - cycle.paidAmountCents
    } else if (cycle.cutoffDate < openCutoff) {
      billedUnpaidCents += Math.max(0, cycle.closingBalanceCents - cycle.paidAmountCents)
    }
  }

  return {
    currentBalanceCents: Math.max(
      0,
      billedUnpaidCents + openCycleCents + unbilledInstallmentsCents,
    ),
    billedUnpaidCents,
    openCycleCents,
    unbilledInstallmentsCents,
  }
}
